import cx from "clsx";
import { PayoutMethod } from "kreate-common/modules/business-types";
import CustomButton from "kreate-common/modules/kreate-ui/components/CustomButton";
import Divider from "kreate-common/modules/kreate-ui/components/Divider";
import DropdownMenu from "kreate-common/modules/kreate-ui/components/DropdownMenu";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import { httpPost$UpdateUser } from "kreate-server/commands/UpdateUser/fetcher";
import Image from "next/image";
import React from "react";
import { MdMoreHoriz } from "react-icons/md";

import styles from "./index.module.scss";

import { useWithErrorHandler } from "@/modules/common-hooks/useWithErrorHandler";
import { shamelesslyRevalidateEverything } from "@/modules/common-utils/shamelesslyRevalidateEverything";
import { useToast } from "@/modules/kreate-contexts/ToastContext";
import { useModalTrigger$ModalConfirm } from "@/modules/kreate-modals/modals/ModalConfirm/hooks";
import { useModalTrigger$ModalFailure } from "@/modules/kreate-modals/modals/ModalFailure/hooks";
import pngBankTransfer from "@/modules/kreate-modals/modals/ModalPayoutAccount/assets/bank-transfer.png";
import pngCrypto from "@/modules/kreate-modals/modals/ModalPayoutAccount/assets/crypto.png";
import pngMomo from "@/modules/kreate-modals/modals/ModalPayoutAccount/assets/momo.png";

type Props = {
  className?: string;
  style?: React.CSSProperties;
  value: PayoutMethod;
  array: PayoutMethod[] | null | undefined;
  index?: number | null | undefined;
};

const ButtonIcon = CustomButton.withDefaultProps({
  color: [styles.buttonColor],
  size: "md-square",
  padding: "minimal",
  border: "solid",
});

function maskPhoneNumber(value: string) {
  if (value.length < 8) return value.replace(/[^\s()-]/g, "*");
  return value.slice(0, -2).replace(/[^\s()-]/g, "*") + value.slice(-2);
}

function maskAccountNumber(value: string) {
  if (value.length < 8) return value.replace(/[^\s-]/g, "*");
  return (
    value.slice(0, 2) +
    value.slice(2, -2).replace(/[^\s-]/g, "*") +
    value.slice(-2)
  );
}

function maskCryptoAddress(value: string) {
  if (value.length < 16) return value.replace(/[^\s-]/g, "*");
  return (
    value.slice(0, 4) +
    value.slice(4, -4).replace(/./g, "*").slice(0, 8) +
    value.slice(-4)
  );
}

export default function CardPayoutAccount({
  className,
  style,
  value,
  array,
  index,
}: Props) {
  const modalTriggers = {
    ModalConfirm: useModalTrigger$ModalConfirm(),
    ModalFailure: useModalTrigger$ModalFailure(),
  };

  const { showMessage } = useToast();

  const { busy, withErrorHandler } = useWithErrorHandler((error) =>
    modalTriggers.ModalFailure.openModalWithError(error)
  );

  const { title, description, image } = (() => {
    switch (value.type) {
      case "momo":
        return {
          title: "Momo",
          description: maskPhoneNumber(value.phoneNumber || ""),
          image: pngMomo,
        };
      case "bank-transfer":
        return {
          title: "Bank Transfer",
          description: maskAccountNumber(value.accountNumber || ""),
          image: pngBankTransfer,
        };
      case "crypto":
        return {
          title: "Crypto",
          description: maskCryptoAddress(value.address || ""),
          image: pngCrypto,
        };
    }
  })();

  return (
    <div className={cx(styles.container, className)} style={style}>
      <Flex.Col
        gap="20px"
        minWidth="428px"
        padding="20px 32px"
        boxSizing="border-box"
      >
        <Flex.Row gap="24px" alignItems="center">
          <Image className={styles.logo} src={image} alt="" />
          <Flex.Cell className={styles.title} flex="1 1 0">
            {title}
          </Flex.Cell>
          <DropdownMenu.Menu
            trigger={<ButtonIcon icon={<MdMoreHoriz />} />}
            disabled={busy}
          >
            <DropdownMenu.Item
              content="Remove"
              onSelect={async () =>
                await withErrorHandler(async () => {
                  const confirmed = await modalTriggers.ModalConfirm.openModal(
                    "Do you want to remove this payout method?",
                    { title: "Remove" }
                  );
                  if (!confirmed || !value || !array) return;
                  await httpPost$UpdateUser(`/api/v1/me/edit`, {
                    payoutMethods: array.filter((_, idx) => idx !== index),
                  });
                  await shamelesslyRevalidateEverything();
                  showMessage({
                    color: "success",
                    title: "Payout method removed successfully!",
                  });
                })()
              }
            />
          </DropdownMenu.Menu>
        </Flex.Row>
        <Divider.Horizontal />
        <div className={styles.description}>{description}</div>
      </Flex.Col>
    </div>
  );
}
